/**
 * Location Store
 *
 * Unified reactive state management for location data using Zustand with slices.
 * Single source of truth for location data, filtering, and selection.
 *
 * Features:
 * - Location data management (tours, markets)
 * - Location selection tracking
 * - Filter state (date, location type, search)
 * - Efficient visibility filtering with ID sets
 * - Computed properties for visible locations
 * - Search persistence (searchPoint, searchQuery restored on page load)
 *
 * Architecture:
 * - Sliced architecture for better organization and maintainability
 * - Single source of truth for all location data
 * - visibleLocationIds: Lightweight Set for O(1) visibility checks
 * - Filter logic centralized in updateVisibleLocations() action
 * - Components subscribe to visibleLocationIds for reactive updates
 *
 * Slices:
 * - LocationDataSlice: Source of truth for location data
 * - VisibilityFilterSlice: Filtering and visibility management
 * - SelectionSlice: User selection state
 * - PersistenceSlice: URL state synchronization
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { createLocationDataSlice, type LocationDataSlice } from './slices/location-data-slice';
import { createVisibilityFilterSlice, type VisibilityFilterSlice } from './slices/visibility-filter-slice';
import { createSelectionSlice, type SelectionSlice } from './slices/selection-slice';
import { createPersistenceSlice, type PersistenceSlice } from './slices/persistence-slice';

// Re-export types from helpers for convenience
export type { SearchPoint, MapBounds } from './helpers';

/**
 * Combined location state interface
 */
export type LocationState = LocationDataSlice &
  VisibilityFilterSlice &
  SelectionSlice &
  PersistenceSlice;

/**
 * Location store instance
 *
 * Composed from individual slices for better organization.
 * Each slice handles a specific domain of the location functionality.
 *
 * Usage:
 * ```typescript
 * import { locationStore } from '@/features/locations/stores/location-store';
 *
 * // Subscribe to changes
 * const unsubscribe = locationStore.subscribe(state => {
 *   console.log('Selected location:', state.getSelectedLocation());
 * });
 *
 * // Get current state
 * const state = locationStore.getState();
 *
 * // Dispatch actions
 * locationStore.getState().selectLocation(123);
 * locationStore.getState().updateFilters({ date: '2024-06-15' });
 * ```
 */
export const locationStore = create<LocationState>()(
  subscribeWithSelector((set, get) => ({
    ...createLocationDataSlice(set, get),
    ...createVisibilityFilterSlice(set, get),
    ...createSelectionSlice(set, get),
    ...createPersistenceSlice(set, get),
  }))
);

/**
 * Hook up cross-slice updates
 *
 * When location data changes, update visibility.
 * This ensures updateVisibleLocations() is called automatically
 * when tours or markets are updated.
 */
locationStore.subscribe(
  (state) => ({ tours: state.tours, markets: state.markets }),
  () => {
    locationStore.getState().updateVisibleLocations();
  },
  {
    equalityFn: (a, b) => a.tours === b.tours && a.markets === b.markets,
  }
);

/**
 * Hook up URL sync to filter changes
 *
 * Automatically sync URL when filters change.
 */
locationStore.subscribe(
  (state) => state.filters,
  () => {
    locationStore.getState().syncToUrl();
  },
  {
    equalityFn: (a, b) =>
      a.date === b.date &&
      a.locationType === b.locationType &&
      a.searchQuery === b.searchQuery,
  }
);

/**
 * Export utility functions for vanilla JS access
 */
export const getLocationState = () => locationStore.getState();
export const subscribeToLocations = (listener: (state: LocationState) => void) =>
  locationStore.subscribe(listener);
